import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import ViewEmployeeDataModal from '../../../modals/ViewEmployeeDataModal'
import { COPILOT_ELIGIBILITY, TONE } from '../../data/constants'
import Chip from '../../components/Chip/Chip'
import EmployeeUserCell from '../../components/EmployeeUserCell/EmployeeUserCell'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubArea from '../../components/SubArea/SubArea'
import SubBlock from '../../components/SubBlock/SubBlock'

const getLicenseName = (license) => license.displayName || license.skuPartNumber || license.skuId || '-'
const getPurchasedUnits = (license) => license.prepaidUnits ?? null
const getLicenseStatusRank = (license) => {
  if (license.isCopilotSku) return 2
  if (license.isCopilotEligibleBaseSku) return 1
  return 0
}

function LicensesTable({ licenses }) {
  const columns = [
    {
      key: 'license',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.COL_LICENSE" />,
      render: (row) => <span className="text-body fw-bold">{getLicenseName(row)}</span>,
      sortable: true,
      sortValue: (row) => getLicenseName(row).toLowerCase(),
    },
    {
      key: 'purchased',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.COL_PURCHASED" />,
      render: (row) => getPurchasedUnits(row) ?? '-',
      sortable: true,
      sortValue: getPurchasedUnits,
    },
    {
      key: 'assigned',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.COL_ASSIGNED" />,
      render: (row) => row.consumedUnits ?? 0,
      sortable: true,
      sortValue: (row) => row.consumedUnits ?? 0,
    },
    {
      key: 'status',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.COL_STATUS" />,
      render: (row) => {
        if (row.isCopilotSku)
          return (
            <Chip tone={TONE.INFO}>
              <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.STATUS_COPILOT_ADDON" />
            </Chip>
          )
        if (row.isCopilotEligibleBaseSku)
          return (
            <Chip tone={TONE.OK}>
              <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.STATUS_ELIGIBLE" />
            </Chip>
          )
        return (
          <Chip tone={TONE.DANGER}>
            <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.STATUS_NOT_ELIGIBLE" />
          </Chip>
        )
      },
      sortable: true,
      sortValue: getLicenseStatusRank,
    },
  ]

  return (
    <ReportTable
      columns={columns}
      rows={licenses.map((license) => ({ ...license, key: license.entityId || license.skuId }))}
      empty={<EmptyState messageId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.NO_LICENSES" />}
    />
  )
}

LicensesTable.propTypes = { licenses: PropTypes.arrayOf(PropTypes.shape({})).isRequired }

export default function CopilotEligibleUsers({ licenses, users, score, userCounts = {}, gap = null }) {
  const selectedCustomer = useSelector(getSelectedCustomer)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const openEmployee = useCallback((employee) => setSelectedEmployee(employee), [])
  const closeEmployee = useCallback(() => setSelectedEmployee(null), [])
  const nonEligibleUsers = useMemo(
    () =>
      users
        .filter((user) => user.eligibility === COPILOT_ELIGIBILITY.NOT_ELIGIBLE)
        .map((user) => ({ ...user, key: user.entityId })),
    [users],
  )
  const notEligibleCount = userCounts.notEligible ?? 0
  const footer = notEligibleCount > nonEligibleUsers.length && nonEligibleUsers.length > 0 && (
    <FormattedMessage
      id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.TABLE_FOOTER"
      values={{ shown: nonEligibleUsers.length, total: notEligibleCount }}
    />
  )

  const nonEligibleColumns = [
    {
      key: 'user',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.COL_USER" />,
      render: (row) => (
        <EmployeeUserCell
          row={row}
          onOpen={openEmployee}
          secondary={row.mail || row.department || row.userPrincipalName}
        />
      ),
      sortable: true,
      sortValue: (row) => (row.displayName || row.userPrincipalName || '').toLowerCase(),
    },
    {
      key: 'current',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.COL_CURRENT_LICENSE" />,
      render: (row) => row.currentLicenseName || '-',
      sortable: true,
      sortValue: (row) => (row.currentLicenseName || '').toLowerCase(),
    },
    {
      key: 'status',
      header: <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.COL_STATUS" />,
      render: () => (
        <Chip tone={TONE.DANGER}>
          <FormattedMessage id="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.STATUS_NOT_ELIGIBLE" />
        </Chip>
      ),
      sortable: false,
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.TITLE"
      subtitleId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
    >
      <SubArea
        titleId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.LICENSES_TITLE"
        descriptionId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.LICENSES_DESC"
      >
        <LicensesTable licenses={licenses} />
      </SubArea>
      <SubArea
        titleId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.TABLE_TITLE"
        descriptionId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.TABLE_DESC"
      >
        <ReportTable
          columns={nonEligibleColumns}
          rows={nonEligibleUsers}
          footer={footer}
          empty={
            <EmptyState
              tone={TONE.OK}
              titleId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.ALL_ELIGIBLE_TITLE"
              messageId="AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.ALL_ELIGIBLE"
            />
          }
        />
      </SubArea>
      {selectedEmployee && (
        <ViewEmployeeDataModal
          show
          onHide={closeEmployee}
          tableRowEmployee={selectedEmployee}
          selectedCustomer={selectedCustomer}
        />
      )}
    </SubBlock>
  )
}

CopilotEligibleUsers.propTypes = {
  licenses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  userCounts: PropTypes.shape({ notEligible: PropTypes.number }),
  gap: PropTypes.shape({ reason: PropTypes.string, requiredLicense: PropTypes.string }),
}
